import { useState } from "react";
import { T, TEAM_COLORS } from "../tokens";

// Small chevron-collapsible tree row.
function TreeNode({ icon, label, depth=0, expandable=false, expanded, onToggle, active, onClick, tag }) {
  return (
    <div onClick={onClick} style={{
      display:"flex",alignItems:"center",gap:6,
      padding:`5px 8px 5px ${8+depth*16}px`,
      cursor: onClick ? "pointer" : "default",
      borderRadius:5,
      background: active ? `${T.accent}18` : "transparent",
      color: active ? T.accent : T.textSec,
      fontSize:12,
    }}>
      {expandable ? (
        <span onClick={(e)=>{e.stopPropagation();onToggle();}} style={{width:12,fontSize:9,color:T.textDim}}>
          {expanded ? "▾" : "▸"}
        </span>
      ) : <span style={{width:12}}/>}
      <span style={{fontSize:12,opacity:0.9}}>{icon}</span>
      <span style={{flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</span>
      {tag && <span style={{fontSize:8,color:T.textDim,background:T.panel,border:`1px solid ${T.border}`,
        borderRadius:4,padding:"1px 5px"}}>{tag}</span>}
    </div>
  );
}

export default function LeftPanel({
  frames, currentIdx, setCurrentIdx,
  events, scenarios, activeView, setActiveView, onAddScenario,
}) {
  const [open, setOpen] = useState({ match:true, whatIf:true });
  const toggle = (k)=>setOpen(o=>({...o,[k]:!o[k]}));

  const half = Math.floor(frames.length/2);
  const jumpTo = (idx)=>setCurrentIdx(Math.min(Math.max(idx,0),frames.length-1));

  // Sprint events split by team — this is what the pipeline actually detects
  // today (speed_kmh crossing a threshold). Pass/shot/tackle/goal event
  // classification isn't produced by the backend yet, so we surface what's
  // real rather than fake counts that look like the mockup.
  const team0Sprints = events.filter(e=>e.team_id===0).length;
  const team1Sprints = events.filter(e=>e.team_id===1).length;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflowY:"auto"}}>
      <div style={{padding:"12px 12px 6px"}}>
        <div style={{fontSize:10,fontWeight:800,letterSpacing:1,color:T.textDim,marginBottom:8}}>
          MATCH STRUCTURE
        </div>

        <TreeNode icon="⚽" label="Match" expandable expanded={open.match} onToggle={()=>toggle("match")} />
        {open.match && <>
          <TreeNode icon="▷" label="First Half" depth={1}
            active={frames[currentIdx] < half}
            onClick={()=>jumpTo(0)} />
          <TreeNode icon="▷" label="Second Half" depth={1}
            active={frames[currentIdx] >= half}
            onClick={()=>jumpTo(half)} />
        </>}

        <TreeNode icon="📋" label="Events" />
        <TreeNode icon="👥" label="Formations" tag="no data" />
        <TreeNode icon="🏃" label="Player Tracks" />
        <TreeNode icon="⚡" label="Tactical Moments" tag="no data" />

        <TreeNode icon="🔀" label="What-If Scenarios" expandable expanded={open.whatIf} onToggle={()=>toggle("whatIf")} />
        {open.whatIf && <>
          {scenarios.map(sc=>(
            <TreeNode key={sc.id} icon="●" label={sc.label} depth={1}
              active={activeView===sc.id}
              onClick={()=>setActiveView(sc.id)} />
          ))}
          <div onClick={onAddScenario} style={{
            display:"flex",alignItems:"center",gap:6,padding:"5px 8px 5px 24px",
            cursor:"pointer",fontSize:12,color:T.accent}}>
            <span style={{fontSize:12}}>＋</span> Add Scenario at {frames.length? "current frame" : "…"}
          </div>
        </>}

        <TreeNode icon="📝" label="Coach Notes" />
      </div>

      <div style={{padding:"10px 12px",borderTop:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:10,fontWeight:800,letterSpacing:1,color:T.textDim}}>KEY EVENTS</div>
          <span style={{fontSize:9,color:T.textDim}}>sprints ≥28km/h</span>
        </div>
        <EventRow color={TEAM_COLORS[0]} label="Team A sprints" count={team0Sprints} />
        <EventRow color={TEAM_COLORS[1]} label="Team B sprints" count={team1Sprints} />
        <EventRow color={T.textDim} label="Pass / Shot / Goal" count="—" note="not yet detected by pipeline" />
      </div>
    </div>
  );
}

function EventRow({ color, label, count, note }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"4px 4px",fontSize:12}} title={note}>
      <span style={{width:8,height:8,borderRadius:2,background:color,flexShrink:0}}/>
      <span style={{flex:1,color:T.textSec,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</span>
      <span style={{color:T.textPri,fontWeight:700,fontSize:12}}>{count}</span>
    </div>
  );
}
